import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useSortingList } from '@Pages/RetroSynthesis/ProjectRoute/hooks';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import RsTable from '@Pages/RetroSynthesis/components/RsTable';
import StepSelectionHeader from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteConditions/components/StepSelectionHeader';
import cn from 'classnames';
import { useLastResults, useTopResults } from './hooks';
import ExamplesTableHeader from './ExamplesTableHeader';
import ExamplesTableItem from './ExamplesTableItem';

import './StepDataExamples.custom.scss';

export const DEFAULT_SORT = {
  property: 'exampleNo',
  direction: 'ASC',
};
/* eslint-disable react-hooks/exhaustive-deps */
function StepDataExamples({
  examples,
  firstPriorityData,
  lastPriorityData,
  stepId,
  viewSelected,
  saveStepSelectionsHandler,
  loadLastExamples,
}) {
  // sate saving of selected rows and control duplicated here to speedup user experience
  const [selectedRows, setSelectedRows] = useState([]);
  const [isViewOnlySelected, setViewOnlySelected] = useState(false);
  const topResultsRequestRef = useRef();
  const lastResultsRequestRef = useRef();
  const topResults = useTopResults(firstPriorityData, topResultsRequestRef, lastResultsRequestRef);
  const lastResults = useLastResults(lastPriorityData, lastResultsRequestRef, firstPriorityData, topResults);
  const [sortedDataList, sortList, setSortList] = useSortingList(examples, DEFAULT_SORT);
  const resultForExample = ({ reactionId }) => (topResults && topResults[reactionId]) || (lastResults && lastResults[reactionId]);

  const saveSelection = (selected, selectedIds, trackingData = false) => {
    saveStepSelectionsHandler({
      stepId,
      selected,
      selectedIds,
      trackingData,
    });
  };

  const selectAllHandler = (selected) => {
    let rows;
    let trackingData = null;
    if (selected) {
      rows = examples.map((e) => e.reactionId);
      trackingData = { selectedResults: 'all' };
    } else {
      rows = [];
    }
    setSelectedRows(rows);
    const viewOnlySelected = rows.length === 0 ? false : isViewOnlySelected;
    if (!viewOnlySelected) {
      setViewOnlySelected(false);
    }
    saveSelection(viewOnlySelected, rows, trackingData);
  };

  const handleExampleSelection = (reactionId, checked) => {
    let rows;
    let trackingData = null;
    if (checked) {
      rows = [...selectedRows, reactionId];
      trackingData = {
        selectedResults: examples
          .filter((e) => e.reactionId === reactionId)
          .map((e) => e.exampleNo)
          .join(','),
      };
    } else {
      rows = selectedRows.filter((v) => v !== reactionId);
    }
    setSelectedRows(rows);
    const viewOnlySelected = rows.length === 0 ? false : isViewOnlySelected;
    if (!viewOnlySelected) {
      setViewOnlySelected(false);
    }
    saveSelection(viewOnlySelected, rows, trackingData);
  };

  const handleViewOnlySelection = (e, checked) => {
    setViewOnlySelected(checked);
    saveSelection(checked, selectedRows, { viewSelected: true });
  };

  const isExampleChecked = (example) => selectedRows.includes(example.reactionId);

  const isExampleVisible = (example) => {
    if (isViewOnlySelected) {
      return selectedRows.includes(example.reactionId);
    }
    return true;
  };

  useEffect(() => {
    if (examples) {
      setSelectedRows(examples.filter((e) => e.selected).map((e) => e.reactionId));
    }
  }, [examples]);

  useEffect(() => {
    setViewOnlySelected(viewSelected);
  }, [viewSelected]);

  useEffect(() => {
    if (firstPriorityData && topResults && !lastPriorityData) {
      // init last examples search after top loaded
      loadLastExamples({ step: stepId, examples, selected: viewSelected });
    }
  }, [topResults]);

  return (
    <>
      <StepSelectionHeader
        selectedRowsCount={selectedRows.length}
        selectAllHandler={selectAllHandler}
        isViewOnlySelected={isViewOnlySelected}
        setViewOnlySelected={handleViewOnlySelection}
        maxRowsCount={examples ? examples.length : 0}
      />
      <div
        className={cn('project-route-step-data', 'project-route-step-data-examples', 'project-route-step-data-examples--with-checkboxes')}>
        {firstPriorityData && topResults ? (
          <RsTable
            containerClassName="project-route-step-data-examples__table-container"
            className="project-route-step-data-examples__table"
            dataList={sortedDataList}
            header={<ExamplesTableHeader sortList={sortList} setSortList={setSortList} />}
            renderItem={(example) => (
              <ExamplesTableItem
                {...example}
                result={resultForExample(example)}
                metadata={firstPriorityData.reactionIds.includes(example.reactionId) ? firstPriorityData : lastPriorityData}
                setChecked={handleExampleSelection}
                isChecked={isExampleChecked(example)}
                isVisible={isExampleVisible(example)}
                renderCheckboxesColumn
              />
            )}
          />
        ) : (
          <SimpleSpinner />
        )}
      </div>
    </>
  );
}

StepDataExamples.propTypes = {
  examples: PropTypes.array,
  firstPriorityData: PropTypes.object,
  lastPriorityData: PropTypes.object,
  saveStepSelectionsHandler: PropTypes.func,
  loadLastExamples: PropTypes.func,
  stepId: PropTypes.string,
  viewSelected: PropTypes.bool,
};

export default StepDataExamples;
